import React, { useContext, useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';
import { EnvelopeOpenIcon } from '@heroicons/react/24/outline';

import app from '../utils/firebaseApp.js';
import { AuthContext } from '../utils/auth.js';
import { authErrorMessage } from '../utils/authRedirect.js';
import AuthLayout from '../components/layout/AuthLayout.js';
import { Button, Field, Input, Callout } from '../ui/index.js';
import useDocumentTitle from '../hooks/useDocumentTitle.js';

function ForgotPassword() {
  useDocumentTitle('Reset password');
  const history = useHistory();
  const { currentUser } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [sentTo, setSentTo] = useState('');

  useEffect(() => {
    if (currentUser) history.replace('/dashboard');
  }, [currentUser, history]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const { email } = event.target.elements;
    const value = email.value.trim();
    setError('');
    setIsLoading(true);
    try {
      await sendPasswordResetEmail(getAuth(app), value);
      setSentTo(value);
    } catch (err) {
      // Do not reveal whether the account exists.
      if (err?.code === 'auth/user-not-found') setSentTo(value);
      else setError(authErrorMessage(err, 'We could not send the reset email. Check the address and try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout
      title={sentTo ? 'Check your inbox' : 'Reset your password'}
      description={sentTo ? undefined : 'Enter the email you signed up with and we will send you a reset link.'}
      footer={
        <>
          Remembered it?{' '}
          <Link to="/login" className="font-semibold text-indigo-600 hover:text-indigo-500">
            Back to sign in
          </Link>
        </>
      }
    >
      {sentTo ? (
        <div className="text-center">
          <span className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600">
            <EnvelopeOpenIcon className="h-6 w-6" aria-hidden="true" />
          </span>
          <p className="mt-4 text-sm text-gray-600">
            If an account exists for <span className="font-medium text-gray-900">{sentTo}</span>, a password reset link is on its way. The link expires in one hour.
          </p>
          <div className="mt-6 flex flex-col gap-2">
            <Button to="/login" size="lg" block>
              Return to sign in
            </Button>
            <Button variant="ghost" size="lg" block onClick={() => setSentTo('')}>
              Use a different email
            </Button>
          </div>
        </div>
      ) : (
        <form className="space-y-5" onSubmit={handleSubmit} noValidate>
          {error && (
            <Callout tone="danger" compact onDismiss={() => setError('')}>
              {error}
            </Callout>
          )}
          <Field label="Email address" htmlFor="email">
            <Input id="email" name="email" type="email" autoComplete="email" required placeholder="you@company.com" size="lg" autoFocus />
          </Field>
          <Button type="submit" size="lg" block loading={isLoading}>
            Send reset link
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

export default ForgotPassword;
